import type { AnchorHTMLAttributes, ReactNode } from 'react';
import React from 'react';
import { Icon } from '@material-ui/core';
import type { Theme } from '@material-ui/core';
import type {
    CommonProps,
    OverridableTypeMap,
} from '@material-ui/core/OverridableComponent';
import classnames from 'classnames';

import { Link } from '../Link';
import { useKlpStyles } from '../Theme';
import type { IKlpStyles } from '../Theme';

export enum ActionLinkType {
    link = 'link',
    linkBack = 'linkBack',
}

interface IOwnProps {
    actionType: keyof typeof ActionLinkType;
    translation: string;
    translationOptions?: object;
    startIcon?: ReactNode;
    endIcon?: ReactNode;
    disabled?: boolean;
}

export type IActionLinkProps = IOwnProps &
    AnchorHTMLAttributes<HTMLAnchorElement> &
    CommonProps<OverridableTypeMap> &
    IKlpStyles;

const styles = (theme: Theme) => ({
    link: {
        paddingTop: theme.spacing(0.5),
    },
    linkDisabled: {
        color: theme.palette.primary.medium,
        '&:before': {
            backgroundColor: theme.palette.primary.medium,
        },
        '&:hover:before': {
            backgroundColor: theme.palette.primary.medium,
        },
    },
    iconStart: {
        cursor: 'pointer',
        marginRight: theme.spacing(0.5),
    },
});

const iconMapStart: Partial<Record<ActionLinkType, string>> = {
    linkBack: 'arrow_back',
};

export const ActionLink: React.FC<IActionLinkProps> = (props) => {
    const classes = useKlpStyles(props, styles);

    const {
        className,
        actionType,
        translation,
        translationOptions,
        startIcon,
        endIcon,
        disabled,
        onClick,
        ...rest
    } = props;

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
        event.preventDefault();
        onClick?.(event);
    };

    const getIcon = (
        icon: ReactNode | undefined,
        type: keyof typeof ActionLinkType,
        position: 'start' | 'end',
    ) => {
        if (React.isValidElement(icon)) {
            return icon;
        }

        if (position === 'start' && iconMapStart[type]) {
            return (
                <Icon className={classes.iconStart} fontSize="inherit">
                    {iconMapStart[type]}
                </Icon>
            );
        }
        return null;
    };

    return (
        <Link
            startIcon={getIcon(startIcon, actionType, 'start')}
            endIcon={getIcon(endIcon, actionType, 'end')}
            onClick={!disabled ? handleClick : undefined}
            translation={translation}
            translationOptions={translationOptions}
            className={classnames(classes.klpStyles, className, {
                [classes.linkDisabled]: disabled,
            })}
            disabled={disabled}
            {...rest}
        />
    );
};
